import styled, { keyframes } from 'styled-components'

import { media } from 'featureSliced/shared/utils/breakpoints'

const opening = keyframes`
  0% { bottom: -95vh; }
  100% { bottom: 0vh; }
`

const closing = keyframes`
  100% { bottom: -95vh; }
  0% { bottom: 0%; }
`

const webOpening = keyframes`
  0% { opacity: 0; transform: translateY(12px) scale(0.98); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`

const webClosing = keyframes`
  0% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(12px) scale(0.98); }
`

const openingOpacity = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

const closingOpacity = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`

export const ModalCoverOverlay = styled('div')<any>`
  position: fixed;
  z-index: 5;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  opacity: 1;
  animation: ${({ isClosing }) => (isClosing ? closingOpacity : openingOpacity)}
    0.3s;

  /* QRM-641: from the web breakpoint the overlay centres its dialog */
  ${media.web} {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
  }
`

export const ModalContainer = styled('div')<any>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100vw;
  max-height: 95vh;
  min-height: 35vh;
  max-height: 95dvh;
  min-height: 35dvh;

  background: ${({ theme }) => theme.MainBackGround};
  /* border-radius: 30px 30px 0 0; */
  border-radius: ${({ withHeader }) =>
    withHeader ? '20px 20px 0 0' : '30px 30px 0 0'};
  overflow-x: hidden;
  overflow-y: scroll;

  display: grid;
  /* grid-template-rows: 60px 1fr; */
  grid-template-rows: ${({ withHeader }) => (withHeader ? '60px 1fr' : '1fr')};
  grid-template-columns: 1fr;

  animation: ${({ isClosing }) => (isClosing ? closing : opening)} 0.3s ease-out; // NOTE: time must be the same as timeout time

  /* QRM-641: a bottom sheet stretched to 1440px is the worst thing on the
     desktop today — from the web breakpoint it becomes a centred dialog.
     dialogWidth lets a caller ask for a wider one (branch lists, product). */
  ${media.web} {
    position: relative;
    bottom: auto;
    left: auto;
    width: ${({ dialogWidth }) => dialogWidth || '560px'};
    max-width: 100%;
    max-height: min(86dvh, 860px);
    min-height: 0;
    border-radius: 20px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);

    /* QRM-662: the dialog itself stops scrolling — its body does. A grid item
       defaults to min-height: auto, so ContentContainer could not shrink below
       its content and the whole box grew instead, taking the footer with it.
       minmax(0, …) is what lets the row shrink to the max-height. */
    overflow: hidden;
    grid-template-rows: ${({ withHeader }) =>
      withHeader ? '60px minmax(0, 1fr)' : 'minmax(0, 1fr)'};
    animation: ${({ isClosing }) => (isClosing ? webClosing : webOpening)} 0.3s
      ease-out;
  }
`

export const ModalHeader = styled.div`
  height: 60px;
  width: 100%;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: transparent;
  /* background-color: red; */
  /* color: #000; */
  color: ${({ theme }) => theme.FontColor};
  padding: 0 20px;

  font-weight: 600;
  size: 16px;
`

export const ModalTransparentHeader = styled.div`
  position: absolute;
  padding: 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  z-index: 7;
  /* a full-width invisible strip: it must not swallow taps meant for what's
     under it (the wishlist heart sits beneath) — only its own buttons are live */
  pointer-events: none;
  > * {
    pointer-events: auto;
  }
`

export const ButtonContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background-color: rgba(0, 0, 0, 0.55);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

export const Title = styled.p`
  font-weight: 600;
`

export const ContentContainer = styled.div`
  /* max-height: calc(85vh - 25px); */
  max-height: calc(95vh - 20px);

  padding: 0;
  overflow: scroll;
  position: relative;
  background-color: ${({ theme }) => theme.MainBackGround};

  &::after {
    content: '';
    width: 100%;
    height: 25px;
    position: fixed;
    background: linear-gradient(
      0deg,
      ${({ theme }) => theme.MainBackGround},
      transparent
    );
    bottom: 0;
    left: 0;
  }

  /* the fade-out strip is fixed to the viewport, which is wrong once the
     dialog is a centred box rather than a full-width sheet; this block must
     stay AFTER the base &::after rule — same specificity, source order wins */
  ${media.web} {
    max-height: none;
    min-height: 0;

    /* QRM-662: this is the scroller now. The base rule sets overflow on BOTH
       axes and only survived because ModalContainer carried the overflow-x
       guard; inherited as-is it would paint permanent scrollbar gutters on
       classic-scrollbar platforms in every dialog. */
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;

    &::after {
      content: none;
    }
  }
`

export const ComponentWrapper = styled.div`
  margin: 0 20px;
  /* padding: 0 0 25px; */
  padding: 25px 0 25px;
  height: 100%;
  /* overflow: auto; */

  /* the phone sheet's bottom padding read as flush against the web dialog's
     rounded corner and shadow (Pavel, 2026-09-21) — more room under the
     content/footer button at the web breakpoint only */
  ${media.web} {
    padding-bottom: 40px;
    /* QRM-662: height: 100% fights the body scroll box it now lives in */
    height: auto;
  }
`
